/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { IMandsBulkOrderData } from './mands-bulk-order.data';
import { IMandsBulkOrderProps } from './mands-bulk-order.props.autogenerated';
import { RichTextComponent } from '@msdyn365-commerce/core';
import SendBulkOrderEmailAction, { SendBulkOrderEmailInput } from '../../actions/send-bulk-order-email';
// @ts-ignore
import ReCAPTCHA from 'react-google-recaptcha';

interface IState {
    yourName?: string;
    position?: string;
    schoolName?: string;
    schoolAddress?: string;
    bestContactNumber?: string;
    contactEmailAddress?: string;
    enquiryDetails?: string;
    errorMessage?: string;
}

interface IBulkOrderFormField {
    fieldLabel: string;
    stateName: string;
    inputType: string;
}
/**
 *
 * MandsBulkOrder component
 * @extends {React.PureComponent<IMandsBulkOrderProps<IMandsBulkOrderData>>}
 */

export default class MandsBulkOrder extends React.PureComponent<IMandsBulkOrderProps<IMandsBulkOrderData>, IState> {
    constructor(props: IMandsBulkOrderProps<IMandsBulkOrderData>) {
        super(props);

        this.state = {
            yourName: '',
            position: '',
            schoolName: '',
            schoolAddress: '',
            bestContactNumber: '',
            contactEmailAddress: '',
            enquiryDetails: '',
            errorMessage: ''
        };
    }

    public renderInputField = (labelName: string, stateName: string, inputOrText: string) => {
        let inputType = 'text';

        switch (stateName) {
            case 'contactEmailAddress':
                inputType = 'email';
                break;
            case 'bestContactNumber':
                inputType = 'number';
                break;
            default:
                inputType = 'text';
        }
        return (
            <div className='row'>
                <div className='col-6'>
                    <label>{labelName}</label>
                    <span className='req'>*</span>
                </div>
                <div className='col-6'>
                    {inputOrText === 'input' && (
                        <div>
                            <input type={inputType} value={this.state[stateName]} onChange={this.onChangeInputState(stateName)} required />
                        </div>
                    )}
                    {inputOrText === 'text' && (
                        <div>
                            <textarea value={this.state[stateName]} onChange={this.onChangeTextAreaState(stateName)} required />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    public onChangeTextAreaState = (stateName: string) => (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ [stateName]: event.target.value });
    };

    public onChangeInputState = (stateName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ [stateName]: event.target.value });
    };

    public onChange = () => {
        // @ts-ignore
        document && document.getElementById('submit-bulkorder').removeAttribute('disabled');
    };

    public sendEmail = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        const { isBulkOrder, mandsEmailAddress = '', redirectLink = '/mandsuniformsandbox', mandsEmailSenderName = '' } = this.props.config;
        const {
            yourName = '',
            position = '',
            schoolName = '',
            schoolAddress = '',
            bestContactNumber = '',
            contactEmailAddress = '',
            enquiryDetails = ''
        } = this.state;
        const subject = isBulkOrder ? 'Bulk Order Enquiry' : 'New School Enquiry';
        const callerContext = this.props.context.actionContext;
        const emailRelayLink = this.props.context.app.config.emailRelayLinkBulkOrder;
        const sendGridTemplateId = this.props.context.app.config.bulkOrdertemplateId;
        const sendEmailInput = new SendBulkOrderEmailInput(
            yourName,
            position,
            schoolName,
            schoolAddress,
            bestContactNumber,
            contactEmailAddress,
            enquiryDetails,
            subject,
            mandsEmailAddress,
            emailRelayLink,
            sendGridTemplateId,
            mandsEmailSenderName
        );
        const emailActionResponse = await SendBulkOrderEmailAction(sendEmailInput, callerContext);
        if (emailActionResponse.status.toString().startsWith('2')) {
            window.location.href = redirectLink;
        } else {
            this.setState({ errorMessage: 'Something went wrong' });
        }
    };

    public render(): JSX.Element {
        const { config } = this.props;
        const {
            enquiryParagraph,
            formHeading,
            contactNumberLabel = '',
            emailAddressLabel = '',
            enquiryDetailsLabel = '',
            nameLabel = '',
            positionLabel = '',
            schoolAddressLabel = '',
            schoolNameLabel = ''
        } = config;
        const captchaSiteKey = this.props.context.app.config.captchaSiteKey;
        const bulkOrderFormFields: IBulkOrderFormField[] = [
            { fieldLabel: nameLabel, stateName: 'yourName', inputType: 'input' },
            { fieldLabel: positionLabel, stateName: 'position', inputType: 'input' },
            { fieldLabel: schoolNameLabel, stateName: 'schoolName', inputType: 'input' },
            { fieldLabel: schoolAddressLabel, stateName: 'schoolAddress', inputType: 'text' },
            { fieldLabel: contactNumberLabel, stateName: 'bestContactNumber', inputType: 'input' },
            { fieldLabel: emailAddressLabel, stateName: 'contactEmailAddress', inputType: 'input' },
            { fieldLabel: enquiryDetailsLabel, stateName: 'enquiryDetails', inputType: 'text' }
        ];

        return (
            <div className='AccountContent'>
                {this.state.errorMessage && <div className='bulk-order-error-message'>{this.state.errorMessage}</div>}
                {formHeading && <h3>{formHeading}</h3>}
                <div className='form-group'>
                    <form onSubmit={this.sendEmail}>
                        <div className='row'>{enquiryParagraph && <RichTextComponent text={enquiryParagraph} />}</div>
                        {bulkOrderFormFields.map(formField =>
                            this.renderInputField(formField.fieldLabel, formField.stateName, formField.inputType)
                        )}

                        {captchaSiteKey && (
                            <div className='captchaDiv'>
                                <ReCAPTCHA sitekey={captchaSiteKey} onChange={this.onChange} dataSize='normal' />
                            </div>
                        )}
                        <div className='btn-box'>
                            <input id='submit-bulkorder' disabled type='submit' value='Submit' />
                        </div>
                    </form>
                </div>
            </div>
        );
    }
}
